/**
 * Admin Service
 * Wraps the admin dashboard API endpoints using the shared axios instance
 * (httpOnly cookie auth via withCredentials).
 */

import api from './api';

const adminService = {
  // ---- Overview / system health ----
  async getOverview() {
    const res = await api.get('/admin/overview');
    return res.data.data;
  },

  async getTraction({ days = 30 } = {}) {
    const res = await api.get('/admin/traction', { params: { days } });
    return res.data.data;
  },

  async getHomeSummary() {
    const res = await api.get('/admin/home-summary');
    return res.data.data;
  },

  async getSystemHealth() {
    const res = await api.get('/admin/system-health');
    return res.data.data;
  },

  // ---- User management ----
  async getUsers({ search, role, status, limit = 25, offset = 0 } = {}) {
    const res = await api.get('/admin/users', { params: { search, role, status, limit, offset } });
    return { users: res.data.users, pagination: res.data.pagination };
  },

  async getUserById(userId) {
    const res = await api.get(`/admin/users/${userId}`);
    return res.data.data;
  },

  async setUserStatus(userId, status, opts = {}) {
    const res = await api.patch(`/admin/users/${userId}/status`, {
      status,
      ...(opts.reason ? { reason: opts.reason } : {}),
      ...(opts.suspendedUntil ? { suspendedUntil: opts.suspendedUntil } : {}),
    });
    return res.data.data;
  },

  /** Every verification assigned to one verifier, across all three flows. */
  async getUserVerificationWork(userId) {
    const res = await api.get(`/admin/users/${userId}/verifications`);
    return res.data.data;
  },

  async getUserBalance(userId) {
    const res = await api.get(`/admin/users/${userId}/balance`);
    return res.data.data;
  },

  async setUserRole(userId, role) {
    const res = await api.patch(`/admin/users/${userId}/role`, { role });
    return res.data.data;
  },

  async deleteUser(userId) {
    const res = await api.delete(`/admin/users/${userId}`);
    return res.data;
  },

  // ---- Projects ----
  async getProjects({ status, search, limit = 25, offset = 0 } = {}) {
    const res = await api.get('/admin/projects', { params: { status, search, limit, offset } });
    return { projects: res.data.projects, pagination: res.data.pagination };
  },

  async getProjectById(projectId) {
    const res = await api.get(`/admin/projects/${projectId}`);
    return res.data.data;
  },

  /**
   * Change a project's status. Uses the existing project route, which already
   * permits an admin caller (routes/projects.js: owner || contractor || admin).
   */
  async setProjectStatus(projectId, status) {
    const res = await api.patch(`/projects/${projectId}/status`, { status });
    return res.data;
  },

  // ---- Verifications ----
  /**
   * Assign a verifier to a work verification. Uses the existing
   * verification-requests route, which admits an admin caller
   * (routes/verificationRequests.js: owner || isAdminUser).
   */
  async assignWorkVerifier(requestId, verifierId) {
    const res = await api.post(`/verification-requests/${requestId}/assign`, {
      verifierId,
      method: 'manual',
    });
    return res.data;
  },

  /**
   * Assign a verifier to a contractor-vetting file. `verificationId` is the
   * contractor_verifications id, not the assignment id.
   */
  async assignContractorVerifier(verificationId, verifierId, opts = {}) {
    const res = await api.post(`/contractor-verifications/admin/${verificationId}/assign-verifier`, {
      verifierId,
      ...opts,
    });
    return res.data;
  },

  /** Take a contractor file off its verifier without reassigning it. */
  async unassignContractorVerifier(verificationId, reason) {
    const res = await api.post(`/contractor-verifications/admin/${verificationId}/unassign-verifier`, { reason });
    return res.data;
  },

  async getVerificationSummary() {
    const res = await api.get('/admin/verifications/summary');
    return res.data.data;
  },

  /** kind: 'work' (verification_requests) | 'contractor' (contractor vetting) */
  /**
   * Full contractor verification file for the admin detail page: company
   * details, owner profile, documents, portfolio (image URLs pre-signed
   * server-side), references, requirements checklist and event history.
   * `verificationId` is the contractor_verifications id.
   */
  /** Approve/reject a canonical user_documents row (same write as the
   * Applications dialog; the verification snapshot reconciles on next read). */
  async reviewUserDocument(documentId, decision, reason) {
    const path = decision === 'approved' ? 'approve' : 'reject';
    const res = await api.post(`/admin/documents/${documentId}/${path}`, reason ? { reason } : {});
    return res.data;
  },

  /** Set status on a verification-snapshot evidence row (documents | portfolio | references). */
  async reviewVerificationEvidence(type, evidenceId, status, reviewerNotes) {
    const res = await api.patch(`/contractor-verifications/admin/evidence/${type}/${evidenceId}`, {
      status, ...(reviewerNotes ? { reviewerNotes } : {}),
    });
    return res.data;
  },

  /**
   * The verifier's field report for a contractor file: the assignment's
   * findings, who they met on site, and each site visit. Separate from
   * getContractorVerificationFile, which only carries what the contractor
   * submitted.
   */
  async getContractorVerificationEvidencePack(verificationId) {
    const res = await api.get(`/contractor-verifications/admin/${verificationId}/evidence-pack`);
    return res.data.data;
  },

  async getContractorVerificationFile(verificationId) {
    const res = await api.get(`/contractor-verifications/admin/${verificationId}`);
    return res.data.data;
  },

  async getVerifications({ kind = 'work', status, search, assigned, limit = 25, offset = 0 } = {}) {
    const res = await api.get('/admin/verifications', {
      params: { kind, status, search, assigned, limit, offset },
    });
    return { verifications: res.data.verifications, pagination: res.data.pagination };
  },

  // ---- Analytics ----
  async getKpis(params = {}) {
    const res = await api.get('/analytics/kpis', { params });
    return res.data.data;
  },

  async getCharts(params = {}) {
    const res = await api.get('/analytics/charts', { params });
    return res.data.data;
  },

  async getRealtime() {
    const res = await api.get('/analytics/realtime');
    return res.data.data;
  },

  // ---- Financial ----
  async getPaymentDistributions({ status, limit = 50, offset = 0 } = {}) {
    const res = await api.get('/payments/admin/all', { params: { status, limit, offset } });
    return res.data.data;
  },

  async getWithdrawals({ status, userRole, limit = 50, offset = 0 } = {}) {
    const res = await api.get('/payments/admin/withdrawals', { params: { status, userRole, limit, offset } });
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async getDistribution() {
    const res = await api.get('/admin/distribution');
    return res.data?.data || res.data;
  },

  // ---- Security & audit ----
  async getSecurityDashboard() {
    const res = await api.get('/admin/security/dashboard');
    return res.data.data || res.data;
  },

  async getSecurityAlerts(params = {}) {
    const res = await api.get('/admin/security/alerts', { params });
    return res.data.data || res.data;
  },

  async getRiskProfiles(params = {}) {
    const res = await api.get('/admin/security/risk-profiles', { params });
    return res.data.data || res.data;
  },

  async resolveAlert(alertId, body = {}) {
    const res = await api.post(`/admin/security/alerts/${alertId}/resolve`, body);
    return res.data;
  },

  // ---- Content management (CMS) ----
  async getContent({ type, status, search, limit = 50, offset = 0 } = {}) {
    const res = await api.get('/content/admin', { params: { type, status, search, limit, offset } });
    return { content: res.data.content, pagination: res.data.pagination };
  },

  async getContentItem(id) {
    const res = await api.get(`/content/admin/${id}`);
    return res.data.data;
  },

  async createContent(payload) {
    const res = await api.post('/content/admin', payload);
    return res.data.data;
  },

  async updateContent(id, payload) {
    const res = await api.put(`/content/admin/${id}`, payload);
    return res.data.data;
  },

  async deleteContent(id) {
    const res = await api.delete(`/content/admin/${id}`);
    return res.data;
  },

  // ---- Signup controls (per-role switches + paused messages) ----
  // WhatsApp brief agent (Admin console → WhatsApp briefs)
  async getWhatsAppBriefSettings() {
    const res = await api.get('/admin/whatsapp-briefs/settings');
    return res.data.data;
  },
  async setWhatsAppBriefMode(mode) {
    const res = await api.put('/admin/whatsapp-briefs/settings', { mode });
    return res.data.data;
  },
  async listWhatsAppBriefSessions(status) {
    const res = await api.get('/admin/whatsapp-briefs', { params: status ? { status } : {} });
    return res.data.data;
  },
  async getWhatsAppBriefCandidates() {
    const res = await api.get('/admin/whatsapp-briefs/candidates');
    return res.data.data;
  },
  async startWhatsAppBrief(userId, seedText) {
    const res = await api.post('/admin/whatsapp-briefs/start', { userId, seedText: seedText || null });
    return res.data.data;
  },
  async previewWhatsAppBriefOutreach(maxAgeDays = 3650) {
    const res = await api.get('/admin/whatsapp-briefs/outreach/preview', { params: { maxAgeDays } });
    return res.data.data;
  },

  async runWhatsAppBriefOutreach(maxAgeDays = 3650) {
    const res = await api.post('/admin/whatsapp-briefs/outreach/run', { maxAgeDays });
    return res.data.data;
  },

  async recomposeWhatsAppBrief(sessionId) {
    const res = await api.post(`/admin/whatsapp-briefs/${sessionId}/recompose`);
    return res.data.data;
  },

  async postWhatsAppBriefForClient(sessionId, note = '') {
    const res = await api.post(`/admin/whatsapp-briefs/${sessionId}/post`, { note });
    return res.data.data;
  },

  async takeOverWhatsAppBrief(sessionId) {
    const res = await api.post(`/admin/whatsapp-briefs/${sessionId}/takeover`);
    return res.data.data;
  },

  async getSignupSettings() {
    const res = await api.get('/admin/signup-settings');
    return res.data.data;
  },

  async updateSignupSetting(role, changes) {
    const res = await api.patch(`/admin/signup-settings/${role}`, changes);
    return res.data;
  },

  // ---- Verifier payment settings ----
  async getVerifierSettings() {
    const res = await api.get('/admin/verifier-settings');
    return res.data.data;
  },

  async updateVerifierSettings(patch) {
    const res = await api.patch('/admin/verifier-settings', patch);
    return res.data;
  },

  // ---- Verifier payouts ----
  async getVerifierPayouts() {
    const res = await api.get('/admin/verifier-payouts');
    return res.data.data;
  },

  async recordVerifierPayout(payload) {
    const res = await api.post('/admin/verifier-payouts/record', payload);
    return res.data;
  },

  async voidVerifierEarning(id, reason) {
    const res = await api.post(`/admin/verifier-earnings/${id}/void`, { reason });
    return res.data;
  },

  // ---- Property listings desk review (Admin → Listings) ----
  async listListings({ status = 'submitted', page = 1, limit = 50 } = {}) {
    const res = await api.get('/admin/listings', { params: { status, page, limit } });
    return res.data.data; // { listings, total }
  },
  async getListing(id) {
    const res = await api.get(`/admin/listings/${id}`);
    return res.data.data;
  },
  async reviewListingItem(id, key, decision, notes) {
    const res = await api.put(`/admin/listings/${id}/requirements/${key}/review`, { decision, notes: notes || null });
    return res.data.data;
  },
  async deskApproveListing(id) { return (await api.post(`/admin/listings/${id}/desk-approve`)).data.data; },
  async returnListing(id, notes) { return (await api.post(`/admin/listings/${id}/return`, { notes: notes || null })).data.data; },
  async rejectListing(id, notes) { return (await api.post(`/admin/listings/${id}/reject`, { notes })).data.data; },

  // ---- Eqaya Verification sign-off (Admin → Listings → one listing) ----
  // The labels for the verifier's findings live in the package scope template,
  // not on the listing — the findings themselves are a bare {fieldId: value}.
  async listingVerificationFields() {
    const res = await api.get('/property-checks/scope-template/listing_verification');
    return res.data?.data?.template?.listingFields || [];
  },
  async markListingVerified(id, dismissals = []) { return (await api.post(`/admin/listings/${id}/mark-verified`, { dismissals })).data.data; },
  async failListingVerification(id, notes) { return (await api.post(`/admin/listings/${id}/fail-verification`, { notes })).data.data; },
  // `force` only travels when asked for: the server treats its presence as an
  // override of a hide Eqaya itself put on the listing.
  async publishListing(id, force = false) { return (await api.post(`/admin/listings/${id}/publish`, force ? { force: true } : {})).data.data; },
  async unpublishListing(id, reason) { return (await api.post(`/admin/listings/${id}/unpublish`, { reason: reason || null })).data.data; },
};

export default adminService;
